import { addOnLines, type AddOnLine } from "../../lib/addOnNotes";

/**
 * Staff view of the homeowner add-ons (upgrades / retail profit / at-cost retail):
 * amount, what the customer was told (description), and the internal note for
 * the crew/office. Mounted next to PreFindingsCard — deal Overview, Chad's build
 * approval queue, Robert's schedule-build modal, PM board — plus Chad's
 * agreement review. Renders nothing when there are no add-ons.
 */
export default function AddOnNotesCard({
  deal,
  className = "",
}: {
  deal: Parameters<typeof addOnLines>[0];
  className?: string;
}) {
  const lines = addOnLines(deal);
  if (lines.length === 0) return null;
  const anyInternal = lines.some((l) => l.internalNote);
  return (
    <div className={`rounded-lg border ${anyInternal ? "border-violet-300 bg-violet-50/60" : "border-slate-200 bg-white"} p-3 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-violet-800">➕ Homeowner add-ons</div>
        {anyInternal && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-200 text-violet-900">internal notes</span>
        )}
      </div>
      <div className="space-y-2">
        {lines.map((l) => <Line key={l.key} l={l} />)}
      </div>
    </div>
  );
}

function Line({ l }: { l: AddOnLine }) {
  const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return (
    <div className="text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium text-bardin-ink">{l.label}</span>
        <span className="font-semibold text-bardin-ink">{l.amount > 0 ? fmt(l.amount) : "—"}</span>
      </div>
      {l.description && (
        <div className="text-xs text-slate-600 mt-0.5">
          <span className="text-slate-400">Customer sees:</span> {l.description}
        </div>
      )}
      {l.internalNote && (
        <div className="text-xs text-violet-900 mt-0.5 whitespace-pre-wrap">
          <span className="font-semibold">🔒 Internal:</span> {l.internalNote}
        </div>
      )}
      {!l.description && !l.internalNote && l.amount > 0 && (
        <div className="text-xs text-amber-700 mt-0.5">No description entered.</div>
      )}
    </div>
  );
}
